import React, { useEffect, useRef, useState, useMemo, useContext } from "react";
import { searchAPI } from "../../utils/apiCalls";
import { FormContext } from "../CMSRenderer/CMSRenderer";

const Search = () => {
  const RESULTS_PER_PAGE = 10;

  const [selectedCategory, setSelectedCategory] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const { currentLang } = useContext(FormContext);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState({
    pages: [],
    news: [],
    faq: [],
    regulatoryDocuments: [],
    services: [],
  });
  const [isLoading, setIsLoading] = useState(false);

  const searchInputRef = useRef(null);
  const isClearing = useRef(false);

  const categories = [
    { key: "all", label: "All", labelAr: "الكل" },
    { key: "services", label: "Services", labelAr: "الخدمات" },
    { key: "news", label: "News", labelAr: "أخبار" },
    { key: "pages", label: "Pages", labelAr: "الصفحات" },
    { key: "faq", label: "FAQs", labelAr: "الأسئلة الشائعة" },
    {
      key: "regulatoryDocuments",
      label: "Policies and Regulations",
      labelAr: "السياسات واللوائح",
    },
  ];

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const queryParam = urlParams.get("query");

    if (queryParam) {
      setSearchQuery(queryParam);

      if (searchInputRef.current) {
        searchInputRef.current.value = queryParam;
      }
    }
  }, []);

  useEffect(() => {
    const handleInputChanged = (e) => {
      if (isClearing.current) {
        return;
      }

      const value = e.target?.value || e.detail?.value || "";

      if (value === "") {
        handleClearSearch();
        return;
      }

      setSearchQuery(value);
    };

    const handleOnChanged = (e) => {
      if (isClearing.current) {
        return;
      }

      const value = e.target?.value || e.detail?.value || "";

      if (value === "") {
        handleClearSearch();
        return;
      }

      setSearchQuery(value);
    };

    const handleClick = (e) => {
      if (e.target.closest('[name="close-button"]')) {
        handleClearSearch();
      }
    };

    const searchElement = searchInputRef.current;

    if (searchElement) {
      searchElement.addEventListener("inputChanged", handleInputChanged);
      searchElement.addEventListener("onChanged", handleOnChanged);
      searchElement.addEventListener("click", handleClick);

      return () => {
        searchElement.removeEventListener(
          "inputChanged",
          handleInputChanged,
        );
        searchElement.removeEventListener("onChanged", handleOnChanged);
        searchElement.removeEventListener("click", handleClick);
      };
    }
  }, []);

  const handleSearchInput = (e) => {
    const value = e.target?.value || "";
    setSearchQuery(value);
  };

  const handleClearSearch = () => {
    setSearchQuery("");

    if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }

    const url = new URL(window.location.href);
    url.searchParams.delete("query");
    window.history.replaceState({}, "", url.pathname);
  };

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setSearchResults({
        pages: [],
        news: [],
        faq: [],
        regulatoryDocuments: [],
        services: [],
      });

      return;
    }

    const debounceTimer = setTimeout(() => {
      performSearch(searchQuery);
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const performSearch = async (query) => {
    setIsLoading(true);

    try {
      const res = await searchAPI({ query });

      if (res) {
        setSearchResults({
          pages: res.pages || [],
          news: res.news || [],
          faq: res.faq || [],
          regulatoryDocuments: res.regulatoryDocuments || [],
          services: res.serviceResults || [],
        });
      }
    } catch (error) {
      console.error("Search failed:", error);

      setSearchResults({
        pages: [],
        news: [],
        faq: [],
        regulatoryDocuments: [],
        services: [],
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChipClick = (category) => {
    setSelectedCategory(category);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory]);

  const filteredResults = useMemo(() => {
    if (selectedCategory === "all") {
      return searchResults;
    }

    return Object.fromEntries(
      Object.keys(searchResults).map((key) => [
        key,
        key === selectedCategory ? searchResults[key] : [],
      ]),
    );
  }, [selectedCategory, searchResults]);

  /*
   * The order of this array controls the order of results
   * when the "All" category is selected.
   */
  const resultSections = [
    {
      key: "services",
      getTitle: (item) =>
        currentLang === "en" ? item.Title : item.TitleAR,
      getDescription: (item) =>
        currentLang === "en"
          ? item.ServiceDescriptionEN
          : item.ServiceDescriptionAR,
      getHref: (item) =>
        `/services/serviceDetails/${item.GUID}`,
    },
    {
      key: "news",
      getTitle: (item) =>
        currentLang === "en" ? item.titleEn : item.titleAr,
      getDescription: (item) =>
        currentLang === "en"
          ? item.shortDescriptionEn
          : item.shortDescriptionAr,
      getHref: (item) =>
        `/mediaCenter/mediaCenterDetails/${item.dguid}`,
    },
    {
      key: "pages",
      getTitle: (item) =>
        currentLang === "en" ? item.title : item.titleAr,
      getHref: (item) => `/${item.slug}`,
    },
    {
      key: "faq",
      getTitle: (item) =>
        currentLang === "en"
          ? item.questionEn
          : item.questionAr,
      getDescription: (item) =>
        currentLang === "en"
          ? item.answerEn
          : item.answerAr,
    },
    {
      key: "regulatoryDocuments",
      getTitle: (item) =>
        currentLang === "en"
          ? item.fileName
          : item.fileNameAr,
      getHref: (item) =>
        currentLang === "en"
          ? item.file
          : item.fileAr,
      openInNewTab: true,
    },
  ];

  /*
   * Convert all category arrays into one ordered list.
   * Each entry keeps its section configuration so it can
   * still be rendered correctly.
   */
  const flatResults = resultSections.flatMap((section) =>
    (filteredResults[section.key] || []).map((item) => ({
      item,
      section,
    })),
  );

  const totalResults = flatResults.length;
  const totalPages = Math.ceil(
    totalResults / RESULTS_PER_PAGE,
  );
  const pageStart =
    (currentPage - 1) * RESULTS_PER_PAGE;

  const paginatedResults = flatResults.slice(
    pageStart,
    pageStart + RESULTS_PER_PAGE,
  );

  const renderResultCard = (item, section, index) => {
    const title = section.getTitle(item) || "";
    const description =
      section.getDescription?.(item) || "";
    const href = section.getHref?.(item);

    const card = (
      <dda-image-card
        custom_class="h-full my-3"
        component_mode=""
        class="hydrated"
      >
        <div>
          <h5
            className="text-primary"
            dangerouslySetInnerHTML={{
              __html: title,
            }}
          ></h5>

          {description && (
            <p
              className="body text-onSurfaceVariant"
              dangerouslySetInnerHTML={{
                __html: description,
              }}
            ></p>
          )}
        </div>
      </dda-image-card>
    );

    if (href) {
      return (
        <a
          key={`${section.key}-${index}`}
          href={href}
          target={
            section.openInNewTab
              ? "_blank"
              : undefined
          }
          rel={
            section.openInNewTab
              ? "noopener noreferrer"
              : undefined
          }
        >
          {card}
        </a>
      );
    }

    return (
      <div key={`${section.key}-${index}`}>
        {card}
      </div>
    );
  };

  return (
    <>
      <dda-breadcrumb
        design="text"
        separator="chevron_right"
        custom_class=""
        component_mode=""
        data-breadcrumbs={`[
          {
            "text": "${
              currentLang === "en"
                ? "Search DHA Website"
                : "ابحث في موقع هيئة الصحة في دبي"
            }",
            "icon": "home",
            "url": "/"
          },
          {
            "text": "${
              currentLang === "en"
                ? "Search DHA Website"
                : "ابحث في موقع هيئة الصحة في دبي"
            }",
            "icon": "menu_book",
            "url": "/search"
          }
        ]`}
      ></dda-breadcrumb>

      <h2 className="mb-4 Medium text-primary">
        {currentLang === "en"
          ? "Search DHA Website"
          : "ابحث في موقع هيئة الصحة في دبي"}
      </h2>

      <dda-search-input
        ref={searchInputRef}
        placeholder={
          currentLang === "en" ? "Search" : "ابحث"
        }
        label={
          currentLang === "en"
            ? "What are you looking for?"
            : "عمَّ تبحث؟"
        }
        size="default"
        helper_text=""
        error_message=""
        validation_id=""
        show_button="true"
        has_error="false"
        custom_class="my-4"
        component_mode=""
        button_id="mainSearch"
        input_id="mainSearchInput"
        aria_label="search-input"
        button_aria_label="clear-search"
        onInput={handleSearchInput}
        close_button_name="close-button"
        search_button_name="search-button"
        button_text={
          currentLang === "en" ? "Search" : "ابحث"
        }
      ></dda-search-input>

      <div className="flex gap-4 my-4 flex-wrap">
        {categories.map((category) => (
          <dda-chip
            size="lg"
            key={category.key}
            chip_text={
              currentLang === "en"
                ? category.label
                : category.labelAr
            }
            bg_color={
              selectedCategory === category.key
                ? "primary"
                : "grey"
            }
            show_close_icon="false"
            onclick={() =>
              handleChipClick(category.key)
            }
            class={`hydrated cursor-pointer ${
              selectedCategory === category.key
                ? ""
                : "*:bg-white"
            }`}
          ></dda-chip>
        ))}
      </div>

      {searchQuery.trim() !== "" && (
        <p className="caption mb-6">
          {currentLang === "en"
            ? "Number of Records"
            : "عدد النتائج التي تم العثور عليها"}
          : {isLoading ? "" : totalResults}
        </p>
      )}

      {isLoading ? (
        <div className="text-center py-8">
          <p className="text-gray-500">
            {currentLang === "en"
              ? "Loading..."
              : "جارٍ التحميل..."}
          </p>
        </div>
      ) : searchQuery.trim() === "" ? (
        <div className="text-center py-8">
          <p className="text-gray-500">
            {currentLang === "en"
              ? "Enter a query to see results"
              : "أدخل استعلامًا لعرض النتائج"}
          </p>
        </div>
      ) : totalResults === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-500">
            {currentLang === "en"
              ? "No results found"
              : "لم يتم العثور على نتائج"}
          </p>
        </div>
      ) : (
        <div className="mb-6">
          {paginatedResults.map(
            ({ item, section }, index) =>
              renderResultCard(
                item,
                section,
                pageStart + index,
              ),
          )}

          {totalPages > 1 && (
            <nav
              className="flex items-center justify-center gap-2 mt-6 flex-wrap"
              aria-label={
                currentLang === "en"
                  ? "Search results pages"
                  : "صفحات نتائج البحث"
              }
            >
              <button
                type="button"
                className="px-4 py-2 border rounded disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={currentPage === 1}
                onClick={() =>
                  setCurrentPage(
                    (page) => page - 1,
                  )
                }
              >
                {currentLang === "en"
                  ? "Previous"
                  : "السابق"}
              </button>

              {Array.from(
                { length: totalPages },
                (_, index) => index + 1,
              ).map((page) => (
                <button
                  type="button"
                  key={page}
                  aria-current={
                    currentPage === page
                      ? "page"
                      : undefined
                  }
                  className={`min-w-10 px-3 py-2 border rounded ${
                    currentPage === page
                      ? "bg-primary text-white"
                      : "bg-white text-primary"
                  }`}
                  onClick={() =>
                    setCurrentPage(page)
                  }
                >
                  {page}
                </button>
              ))}

              <button
                type="button"
                className="px-4 py-2 border rounded disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={
                  currentPage === totalPages
                }
                onClick={() =>
                  setCurrentPage(
                    (page) => page + 1,
                  )
                }
              >
                {currentLang === "en"
                  ? "Next"
                  : "التالي"}
              </button>
            </nav>
          )}
        </div>
      )}
    </>
  );
};

export default Search;